/**
 * <pc-image-slider>
 *
 * Touch/click slider for product card images (up to 3).
 * Ported from happy-ears' product-cardImageSlider, refactored as ES module
 * + Custom Element for sparv-2026's architecture.
 *
 * Behavior:
 *  - Horizontal drag/swipe with rubber-band resistance at edges
 *  - Direction lock (horizontal vs vertical) to allow page scroll
 *  - ResizeObserver to keep transform in sync after layout changes
 *  - prefers-reduced-motion: snaps without animation
 *  - Indicator dots (mobile) + prev/next buttons (desktop)
 *  - Propagation guards so parent swipers don't intercept
 *  - Suppresses synthesized click on parent <a> after a swipe
 *
 * Loaded from snippets/product-card.liquid (per-section pattern).
 */

class PCImageSlider extends HTMLElement {
  #track = null;
  #slides = [];
  #dotIndicator = null;
  #prevBtn = null;
  #nextBtn = null;
  #parentLink = null;

  #activeIndex = 0;
  #isDragging = false;
  #startX = 0;
  #startY = 0;
  #dragX = 0;
  #directionLocked = null;
  #didSwipe = false;

  #resizeObserver = null;
  #rafPending = false;

  #onTouchStart = null;
  #onTouchMove = null;
  #onTouchEnd = null;
  #onPrev = null;
  #onNext = null;
  #onStopProp = null;
  #onLinkClick = null;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.#track = this.querySelector('[data-pc-image-slider-scroller]');
    this.#slides = Array.from(this.querySelectorAll('.product-card__slide'));
    this.#dotIndicator = this.querySelector('.product-card__slider-dot-indicator');
    this.#prevBtn = this.querySelector('[data-action="pc-slide-prev"]');
    this.#nextBtn = this.querySelector('[data-action="pc-slide-next"]');
    this.#parentLink = this.closest('a');

    if (!this.#track || this.#slides.length < 2) return;

    this.style.setProperty('--slider-dot-count', this.#slides.length);

    this.#bindEvents();
    this.#syncSlideWidths();
    this.#setupResizeObserver();
    this.#goTo(0, false);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.removeEventListener('touchstart', this.#onTouchStart, { capture: true });
    this.removeEventListener('touchmove', this.#onTouchMove, { capture: true });
    this.removeEventListener('touchend', this.#onTouchEnd, { capture: true });
    this.removeEventListener('touchcancel', this.#onTouchEnd, { capture: true });
    this.removeEventListener('touchstart', this.#onStopProp, { capture: true });
    this.removeEventListener('touchmove', this.#onStopProp, { capture: true });

    this.#prevBtn?.removeEventListener('click', this.#onPrev);
    this.#nextBtn?.removeEventListener('click', this.#onNext);
    this.#parentLink?.removeEventListener('click', this.#onLinkClick, { capture: true });
  }

  #prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  #bindEvents() {
    this.#onTouchStart = this.#handleTouchStart.bind(this);
    this.#onTouchMove = this.#handleTouchMove.bind(this);
    this.#onTouchEnd = this.#handleTouchEnd.bind(this);
    this.#onPrev = this.#handlePrev.bind(this);
    this.#onNext = this.#handleNext.bind(this);
    this.#onStopProp = (e) => e.stopPropagation();
    this.#onLinkClick = (e) => {
      if (this.#didSwipe) {
        e.preventDefault();
        e.stopPropagation();
        this.#didSwipe = false;
      }
    };

    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, capture: true });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: false, capture: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true, capture: true });
    this.addEventListener('touchcancel', this.#onTouchEnd, { passive: true, capture: true });

    this.addEventListener('touchstart', this.#onStopProp, { passive: true, capture: true });
    this.addEventListener('touchmove', this.#onStopProp, { passive: true, capture: true });

    this.#prevBtn?.addEventListener('click', this.#onPrev);
    this.#nextBtn?.addEventListener('click', this.#onNext);

    this.#parentLink?.addEventListener('click', this.#onLinkClick, { capture: true });
  }

  #setupResizeObserver() {
    if (!window.ResizeObserver) return;
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#isDragging || this.#rafPending) return;
      this.#rafPending = true;
      requestAnimationFrame(() => {
        this.#rafPending = false;
        this.#syncSlideWidths();
        this.#goTo(this.#activeIndex, false);
      });
    });
    this.#resizeObserver.observe(this);
    this.#syncSlideWidths();
  }

  #syncSlideWidths() {
    const w = Math.ceil(this.getBoundingClientRect().width);
    if (!w) return;
    this.#slides.forEach((slide) => {
      slide.style.flex = '0 0 auto';
      slide.style.width = `${w}px`;
    });
    if (this.#track) {
      this.#track.style.width = `${w * this.#slides.length}px`;
    }
  }

  #handleTouchStart(e) {
    if (this.#isDragging) return;
    const t = e.touches?.[0];
    if (!t) return;

    this.#startX = t.clientX;
    this.#startY = t.clientY;
    this.#dragX = t.clientX;
    this.#directionLocked = null;
    this.#didSwipe = false;
    this.#isDragging = true;
    this.#setTransition(false);
    this.setAttribute('data-dragging', 'true');
  }

  #handleTouchMove(e) {
    if (!this.#isDragging) return;
    const t = e.touches?.[0];
    if (!t) return;

    const dx = t.clientX - this.#startX;
    const dy = t.clientY - this.#startY;

    if (this.#directionLocked === null && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      this.#directionLocked = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
    }

    if (this.#directionLocked === 'v') return;
    if (this.#directionLocked === 'h') {
      e.preventDefault();
      this.#didSwipe = true;
    }

    this.#dragX = t.clientX;

    const slideW = Math.ceil(this.getBoundingClientRect().width) || 1;
    const baseOffset = -(this.#activeIndex * slideW);
    let newOffset = baseOffset + dx;

    const atFirst = this.#activeIndex === 0 && dx > 0;
    const atLast = this.#activeIndex === this.#slides.length - 1 && dx < 0;
    if (atFirst || atLast) newOffset = baseOffset + dx * 0.18;

    this.#setTrackOffset(newOffset);
  }

  #handleTouchEnd() {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.removeAttribute('data-dragging');

    if (this.#directionLocked !== 'h') {
      this.#goTo(this.#activeIndex);
      return;
    }

    const dx = this.#dragX - this.#startX;
    const slideW = Math.ceil(this.getBoundingClientRect().width) || 1;
    const threshold = Math.min(slideW * 0.08, 40);

    if (dx < -threshold && this.#activeIndex < this.#slides.length - 1) {
      this.#goTo(this.#activeIndex + 1);
    } else if (dx > threshold && this.#activeIndex > 0) {
      this.#goTo(this.#activeIndex - 1);
    } else {
      this.#goTo(this.#activeIndex);
    }

    // Clear didSwipe flag after a short delay so the synthesized click (if any) is suppressed.
    setTimeout(() => { this.#didSwipe = false; }, 50);
  }

  #handlePrev(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.#activeIndex > 0) this.#goTo(this.#activeIndex - 1);
  }

  #handleNext(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.#activeIndex < this.#slides.length - 1) this.#goTo(this.#activeIndex + 1);
  }

  #goTo(index, animate = true) {
    index = Math.max(0, Math.min(index, this.#slides.length - 1));
    this.#activeIndex = index;

    const slideW = Math.ceil(this.getBoundingClientRect().width) || 0;
    this.#setTransition(animate && !this.#prefersReducedMotion());
    this.#setTrackOffset(-(index * slideW));
    this.#sync();
  }

  #setTransition(on) {
    const transition = on
      ? 'transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      : 'none';
    this.#track.style.transition = transition;
    if (this.#dotIndicator) this.#dotIndicator.style.transition = transition;
  }

  #setTrackOffset(px) {
    this.#track.style.transform = `translate3d(${Math.round(px)}px, 0, 0)`;

    const slideW = Math.ceil(this.getBoundingClientRect().width) || 1;
    const progress = Math.max(0, Math.min(-px / slideW, this.#slides.length - 1));
    if (this.#dotIndicator) {
      this.#dotIndicator.style.transform = `translateX(${progress * 100}%)`;
    }
  }

  #sync() {
    this.#slides.forEach((slide, i) => {
      if (i === this.#activeIndex) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    this.#prevBtn?.setAttribute('aria-disabled', this.#activeIndex === 0 ? 'true' : 'false');
    this.#nextBtn?.setAttribute('aria-disabled', this.#activeIndex === this.#slides.length - 1 ? 'true' : 'false');
  }

  // Public API
  goTo(index) { this.#goTo(index); }
  get activeIndex() { return this.#activeIndex; }
  get slideCount() { return this.#slides.length; }
}

if (!customElements.get('pc-image-slider')) {
  customElements.define('pc-image-slider', PCImageSlider);
}
